'use client';

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { navigationLogger } from '@/core/utils/logger';
import { type LoadingMessageContext } from './useRotatingLoadingMessage';

// Map navigation targets to loading message contexts
const TARGET_TO_CONTEXT: Record<string, LoadingMessageContext> = {
  '/company-info': 'accountSetup',
  '/payment-setup': 'paymentAuth',
  '/setup-complete': 'setupComplete',
  '/dashboard': 'generic',
  '/signup': 'generic',
  '/login': 'generic',
};

// Get the loading message context for a navigation target
export function getLoadingContext(target: string | null): LoadingMessageContext {
  if (!target) return 'generic';
  return TARGET_TO_CONTEXT[target] || 'generic';
}

// Legacy function for backwards compatibility - returns first message of the context
export function getTransitionMessage(target: string | null): string {
  const context = getLoadingContext(target);
  // Return first message as default (actual rotation happens in component)
  const FIRST_MESSAGES: Record<LoadingMessageContext, string> = {
    accountSetup: 'Setting up your account...',
    companyInfo: 'Loading your account...',
    paymentAuth: 'Loading payment options...',
    packages: 'Loading available packages...',
    paymentProcessing: 'Processing payment...',
    setupComplete: 'Finalizing your setup...',
    demo: 'Loading product tour...',
    generic: 'Loading...',
  };
  return FIRST_MESSAGES[context];
}

interface OnboardingNavigationContextValue {
  isNavigating: boolean;
  navigationTarget: string | null;
  startNavigation: (target: string) => void;
  completeNavigation: () => void;
  navigateWithLoading: (target: string, options?: { prefetch?: () => Promise<void> }) => Promise<void>;
}

const OnboardingNavigationContext = createContext<OnboardingNavigationContextValue | null>(null);

export function useOnboardingNavigation() {
  const context = useContext(OnboardingNavigationContext);
  if (!context) {
    // Return safe defaults if not in provider
    return {
      isNavigating: false,
      navigationTarget: null,
      startNavigation: () => {},
      completeNavigation: () => {},
      navigateWithLoading: async () => {},
    };
  }
  return context;
}

export function useOnboardingNavigationProvider() {
  const router = useRouter();
  const pathname = usePathname();
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState<string | null>(null);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPathnameRef = useRef(pathname);

  // Clear navigation state when pathname changes (navigation completed)
  useEffect(() => {
    if (pathname !== lastPathnameRef.current) {
      const previousPathname = lastPathnameRef.current;
      lastPathnameRef.current = pathname;

      // If we're navigating and the pathname changed, clear the navigation state
      // This handles all navigation completions, including edge cases like trailing slashes
      if (isNavigating && navigationTarget) {
        // Small delay to allow the new page to start rendering
        const timer = setTimeout(() => {
          // Clear navigation state - the page has changed
          navigationLogger.debug('[OnboardingNavigation] Pathname changed from', previousPathname, 'to', pathname, '- clearing navigation state');
          setIsNavigating(false);
          setNavigationTarget(null);

          // Also clear the safety timeout since navigation completed
          if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
            navigationTimeoutRef.current = null;
          }
        }, 50); // Reduced from 100ms for faster UI response
        return () => clearTimeout(timer);
      }
    }
  }, [pathname, navigationTarget, isNavigating]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const startNavigation = useCallback((target: string) => {
    setIsNavigating(true);
    setNavigationTarget(target);

    // Safety timeout - if navigation takes more than 5 seconds, reset
    // Reduced from 15s to 5s for better UX on slow connections
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    navigationTimeoutRef.current = setTimeout(() => {
      navigationLogger.warn('[OnboardingNavigation] Navigation timeout (5s), resetting state for', target);
      setIsNavigating(false);
      setNavigationTarget(null);
    }, 5000);
  }, []);

  const completeNavigation = useCallback(() => {
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    setIsNavigating(false);
    setNavigationTarget(null);
  }, []);

  const navigateWithLoading = useCallback(async (
    target: string, 
    options?: { prefetch?: () => Promise<void> }
  ) => {
    startNavigation(target);
    
    try {
      // Prefetch the page bundle
      router.prefetch(target);
      
      // Run any custom prefetch operations
      if (options?.prefetch) {
        await options.prefetch();
      }
      
      // Small delay to ensure prefetch has started
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Navigate
      router.replace(target);
    } catch (error) {
      navigationLogger.error('[OnboardingNavigation] Navigation error:', error);
      completeNavigation();
      throw error;
    }
  }, [router, startNavigation, completeNavigation]);

  return {
    isNavigating,
    navigationTarget,
    startNavigation,
    completeNavigation,
    navigateWithLoading,
    Provider: OnboardingNavigationContext.Provider,
  };
}

export { OnboardingNavigationContext };
